import React from 'react';
import PropTypes from 'prop-types';

import { useDispatch, useSelector } from 'react-redux';

import * as actionsFiles from '../../actions/FilesActions';
import { downloadFileFromURl } from '../../actions/selectors';

require('./UserGuideHelp.scss');

const GUIDE_SCOPES = ['portal', 'mobile'];

export default function UserGuideHelp(props, context) {
  const { triggerLabel = 'help' } = props;
  const [helpOpen, setHelpOpen] = React.useState(false);
  const [activeScope, setActiveScope] = React.useState('');
  const [isDownloadingGuide, setIsDownloadingGuide] = React.useState(false);
  const dispatch = useDispatch();
  const helpMenuRef = React.useRef(null);

  const currentLang = useSelector((state) => state.i18nState.lang || 'en');
  const selectedProject = useSelector(
    (state) => state.leaksList?.selectedProject
  );
  const isFetchingGuides = useSelector(
    (state) => state.leaksList?.optionList?.isFetching
  );
  const userGuides = useSelector(
    (state) => state.leaksList?.optionList?.options?.userGuides || {}
  );

  React.useEffect(() => {
    if (!helpOpen) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      if (helpMenuRef.current && !helpMenuRef.current.contains(event.target)) {
        setHelpOpen(false);
        setActiveScope('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [helpOpen]);

  React.useEffect(() => {
    if (!helpOpen) {
      setActiveScope('');
    }
  }, [helpOpen]);

  const handleGuideDownload = async (scope, guide) => {
    if (!scope || !guide?.name || !selectedProject || isDownloadingGuide) {
      return;
    }

    const normalizedGuideName = guide.name.endsWith('.pdf')
      ? guide.name
      : `${guide.name}.pdf`;
    const guideLanguage = guide.lang || 'en';
    const filePath = `user-guides/${scope}/${guideLanguage}/${normalizedGuideName}`;

    setIsDownloadingGuide(true);
    try {
      dispatch(
        actionsFiles.getUrlForDownloadFile(
          selectedProject,
          filePath,
          'manual',
          '0',
          (res) => {
            if (res?.status && res?.data) {
              downloadFileFromURl(res.data, normalizedGuideName);
              setHelpOpen(false);
              setActiveScope('');
            }
          }
        )
      );
    } finally {
      setIsDownloadingGuide(false);
    }
  };

  const guidesByScope = React.useMemo(
    () => ({
      portal: userGuides.portal || [],
      mobile: userGuides.mobile || [],
    }),
    [userGuides]
  );

  return (
    <div className='lang-select user-guide-help' ref={helpMenuRef}>
      <button
        type='button'
        className='help-trigger'
        onClick={() => setHelpOpen((isOpen) => !isOpen)}
      >
        {triggerLabel}
      </button>
      {helpOpen && (
        <div className='help-menu-dropdown'>
          {GUIDE_SCOPES.map((scope) => {
            const guides = guidesByScope[scope] || [];
            const isActive = activeScope === scope;
            const title = scope === 'portal' ? 'Portal' : 'Mobile';

            return (
              <div
                key={scope}
                className={`help-menu-item${isActive ? ' is-active' : ''}`}
                onMouseEnter={() => setActiveScope(scope)}
                onMouseLeave={() => {
                  if (activeScope === scope) {
                    setActiveScope('');
                  }
                }}
              >
                <button type='button' className='help-scope-trigger'>
                  <span>{title}</span>
                  <span>&gt;</span>
                </button>
                {isActive && (
                  <div className='help-submenu'>
                    {isFetchingGuides && guides.length === 0 ? (
                      <div className='help-guide-empty'>
                        {context.t('loading')}
                      </div>
                    ) : guides.length > 0 ? (
                      guides.map((guide) => (
                        <button
                          key={`${scope}-${guide.name}`}
                          type='button'
                          className='help-guide-item'
                          onClick={() => handleGuideDownload(scope, guide)}
                          disabled={isDownloadingGuide}
                        >
                          {guide.name}
                        </button>
                      ))
                    ) : (
                      <div className='help-guide-empty'>
                        {context.t('no_user_guides_available')}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

UserGuideHelp.contextTypes = {
  t: PropTypes.func.isRequired,
};

UserGuideHelp.propTypes = {
  triggerLabel: PropTypes.string,
};
